import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, QrCode, Zap, Shield } from "lucide-react";
import { api } from "@/lib/api";

const HERO_BG =
  "https://images.pexels.com/photos/13345808/pexels-photo-13345808.jpeg";

export default function Landing() {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-slate-900">
      {/* Hero background (image with light overlay for readability) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white" aria-hidden />
      <div className="absolute inset-0 bg-silver-stripes" aria-hidden />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-5 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-[#1d4ed8] text-white border-2 border-slate-900">
            <span className="font-heading text-xl">G</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading text-lg tracking-wider text-slate-900">
              GAITHER COWBOYS
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Athletic Boosters
            </div>
          </div>
        </div>
        <button
          data-testid="admin-link"
          onClick={() => navigate("/admin")}
          className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#1d4ed8] transition-colors"
        >
          Staff
        </button>
      </header>

      {/* Hero */}
      <main className="relative z-10 mx-auto flex max-w-3xl flex-col items-start px-5 pb-32 pt-10 sm:px-10 sm:pt-20">
        <div className="fade-up mb-6 inline-flex items-center gap-2 border border-slate-300 bg-white/80 px-3 py-1.5 backdrop-blur">
          <span className="h-1.5 w-1.5 animate-pulse bg-[#16a34a]" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-slate-600">
            Live · Friday Night Lights
          </span>
        </div>

        <h1
          className="fade-up font-heading text-5xl leading-[0.95] sm:text-7xl lg:text-8xl text-slate-900"
          style={{ animationDelay: "0.05s" }}
        >
          SKIP THE LINE.
          <br />
          <span className="text-[#1d4ed8]">RIDE WITH THE COWBOYS.</span>
        </h1>

        <p
          className="fade-up mt-6 max-w-xl text-base text-slate-600 sm:text-lg"
          style={{ animationDelay: "0.15s" }}
        >
          Order concessions from your seat. Pay in seconds. Pick up at the
          Cowboys Express Window when your number's called.
        </p>

        <button
          data-testid="start-order-btn"
          onClick={() => navigate("/menu")}
          className="fade-up cart-pulse mt-10 inline-flex items-center gap-3 bg-[#1d4ed8] px-8 py-5 font-heading text-2xl tracking-wider text-white transition-all hover:bg-[#1e40af] active:scale-[0.98] border-2 border-slate-900"
          style={{ animationDelay: "0.25s" }}
        >
          START ORDER
          <ArrowRight className="h-6 w-6" strokeWidth={2.5} />
        </button>

        <button
          data-testid="landing-feedback-link"
          onClick={() => navigate("/feedback")}
          className="fade-up mt-4 text-sm text-slate-600 underline decoration-slate-400 decoration-1 underline-offset-4 transition-colors hover:text-[#1d4ed8] hover:decoration-[#1d4ed8]"
          style={{ animationDelay: "0.3s" }}
        >
          Leave feedback about your visit
        </button>

        {/* Sponsor strip — sits between the CTA and the info cards so
            supporters get real estate without shoving the button offscreen.
            Hidden entirely when no active sponsors are configured. */}
        {sponsors.length > 0 && (
          <div
            data-testid="sponsor-strip"
            className="fade-up mt-12 w-full max-w-3xl border-2 border-slate-900 bg-white/90 px-5 py-4 backdrop-blur"
            style={{ animationDelay: "0.32s" }}
          >
            <div className="mb-3 text-center text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Proudly supported by
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {sponsors.map((s) => {
                const inner = s.logo_url ? (
                  <img
                    src={s.logo_url}
                    alt={s.name}
                    className="h-12 max-w-[160px] object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="font-heading text-base tracking-wider text-slate-800 hover:text-[#1d4ed8]">
                    {s.name}
                  </span>
                );
                return s.website ? (
                  <a
                    key={s.id}
                    href={s.website}
                    target="_blank"
                    rel="noreferrer"
                    title={s.name}
                    data-testid={`sponsor-link-${s.id}`}
                  >
                    {inner}
                  </a>
                ) : (
                  <span key={s.id} title={s.name} data-testid={`sponsor-item-${s.id}`}>
                    {inner}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div
          className="fade-up mt-16 grid w-full grid-cols-3 gap-px border-2 border-slate-900 bg-slate-900"
          style={{ animationDelay: "0.35s" }}
        >
          {[
            { icon: QrCode, label: "Scan to order", value: "No app" },
            { icon: Zap, label: "Pickup time", value: "~5 min" },
            { icon: Shield, label: "Supports", value: "Boosters" },
          ].map((f) => (
            <div
              key={f.label}
              className="flex flex-col items-start gap-2 bg-white p-4 sm:p-6"
            >
              <f.icon className="h-5 w-5 text-[#1d4ed8]" strokeWidth={2} />
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {f.label}
              </div>
              <div className="font-heading text-xl tracking-wider sm:text-2xl text-slate-900">
                {f.value}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Bottom stripe with a link to the About page (SEO-rich prose moved
          off the hero to keep the CTA focused). */}
      <div className="relative z-10 border-t-2 border-slate-900 bg-white/95 px-5 py-3 backdrop-blur sm:px-10">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-slate-600">
          <span>Gaither High · Tampa, FL</span>
          <button
            data-testid="learn-more-link"
            onClick={() => navigate("/about")}
            className="text-[#1d4ed8] hover:text-[#1e40af] underline underline-offset-4"
          >
            Learn more · How it works
          </button>
          <span className="text-[#1d4ed8]">EST. 1959</span>
        </div>
      </div>
    </div>
  );
}
